<template>
  <base-modal size="xl" ref="modal" title="Redeem Credits" @hiding="reset()">
    <base-card shadow noPadding>
      <div v-if="isSuccess === false" class="sm:tw-flex sm:tw-items-start tw-px-4 tw-pt-4">
        <div
          class="tw-flex tw-flex-shrink-0 tw-mx-auto tw-justify-center tw-items-center tw-bg-primary-50 tw-w-12 tw-h-12 tw-rounded-full sm:tw-mx-0 sm:tw-h-10 sm:tw-w-10 sm:tw-my-auto"
        >
          <shopping-bag-icon class="tw-w-5 tw-h-5 tw-text-primary-500" />
        </div>
        <div class="sm:tw-flex-col sm:tw-items-start sm:tw-ml-2 sm:tw-justify-start tw-mt-3 sm:tw-mt-0 tw-text-center sm:tw-text-left">
          <div class="tw-font-thin tw-text-xl">{{ title }}</div>
          <div class="tw-text-gray-600 tw-font-light">{{ description }}</div>
        </div>
      </div>

      <!-- Credit Selection -->
      <div v-if="isConfirming === false && isSuccess === false" class="tw-px-4 tw-pb-4">
        <div class="tw-flex tw-justify-center tw-mt-6 tw-gap-6">
          <base-icon class="tw-my-auto tw-bg-primary-50" color="primary" @click="subtract"> <minus-icon class="tw-h-6 tw-w-6"/></base-icon>
          <base-text-field type="number" class="tw-text-center tw-w-24" v-model="redeemCount" />
          <base-icon class="tw-my-auto tw-bg-primary-50" color="primary" @click="add"> <plus-icon class="tw-h-6 tw-w-6"/></base-icon>
        </div>

        <div v-if="showNegativeCreditNotAllowed" class="tw-rounded-lg tw-bg-red-100 tw-text-red-500 tw-p-3 tw-mt-2">
          Your management does not allow negative credits, please adjust or recharge credits before attempting to redeem more than
          <strong class="tw-text-lg tw-font-normal">{{ plan.creditsBalance }}</strong> credits.
        </div>
        <div v-if="showInactiveMessage" class="tw-rounded-lg tw-bg-red-100 tw-text-red-500 tw-p-3 tw-mt-2">
          This plan is no longer active and only {{ plan.creditsBalance }} credits are available.
        </div>
        <div v-if="showNegativeCreditsWarning" class="tw-rounded-lg tw-bg-yellow-200 tw-text-gray-600 tw-p-3 tw-mt-2">
          Caution: This {{ organization.customerLabelSingular }} currently has <strong class="tw-text-lg tw-font-normal">{{ plan.creditsBalance }}</strong> available credits. Be
          aware that this will leave the {{ organization.customerLabelSingular }} with a negative credit balance, and their total credits will be refilled in the next billing
          cycle.
        </div>
        <div v-if="showRechargeAndRedeemWarning" class="tw-rounded-lg tw-bg-yellow-200 tw-text-gray-600 tw-p-3 tw-mt-2">
          Caution: This {{ organization.customerLabelSingular }} currently has <strong class="tw-text-lg tw-font-normal">{{ plan.creditsBalance }}</strong> available credits. Be
          aware that this {{ organization.customerLabelSingular }} will be charged for {{ rechargeAndRedeemCreditsCount }} credits.
        </div>
        <div v-if="showMaxCreditWarning" class="tw-rounded-lg tw-bg-red-100 tw-text-red-500 tw-p-3 tw-mt-2">
          Stop: Your management does not allow more than {{ maxCreditsSetting.numValue }} credits to be redeemed at once.
        </div>
        <div v-if="showLocationError" class="tw-rounded-lg tw-bg-red-100 tw-text-red-500 tw-p-3 tw-mt-2">You must indicate your current location from the dropdown.</div>
        <base-form v-if="locations.length > 1 && !location" class="tw-mt-4">
          <base-select
            v-model="activeLocation"
            label="Store location"
            placeholder="Select your current store location"
            :options="locations"
            label-key="name"
            value-key="businessLocationId"
          />
        </base-form>
        <inline-error class="tw-mt-3" id="redeem-credits-modal-error" :title="errorTitle" :message="errorMessage" :error="error" />
        <div class="sm:tw-flex sm:tw-flex-row-reverse tw-mt-6">
          <base-button
            v-if="!memberConfirmSetting.boolValue"
            @click="redeemCredits"
            :disabled="!isCreditAmountValid"
            :loading="isLoading"
            class="tw-w-full sm:tw-w-auto sm:tw-ml-3"
          >
            Redeem {{ redeemCount }} {{ redeemCount == 1 ? "credit" : "credits" }}
          </base-button>
          <base-button v-else class="tw-w-full sm:tw-w-auto sm:tw-ml-3" @click="startConfirmation" :disabled="!isCreditAmountValid">Continue</base-button>
          <base-button @click="hide" type="flat" color="gray" class="tw-w-full sm:tw-w-auto tw-mt-2 sm:tw-mt-0">
            Cancel
          </base-button>
        </div>
      </div>

      <!-- Confirmation -->
      <div v-else-if="isConfirming && isSuccess === false" class="tw-px-4 tw-pb-4">
        <div class="tw-flex tw-flex-col">
          <span class="tw-text-lg tw-mt-6">Order Details</span>
          <div class="tw-border tw-rounded-lg tw-mt-2 tw-p-3">
            <div class="tw-flex tw-justify-between">
              <div>
                <div class="tw-text-gray-500 tw-my-auto">{{ memberFullName }}</div>
                <div class="tw-text-gray-500 tw-text-sm tw-my-auto">{{ plan.planName }}</div>
              </div>
              <base-badge color="primary" class="tw-my-auto">{{ redeemCount }} {{ redeemCount === 1 ? "credit" : "credits" }}</base-badge>
            </div>
          </div>
        </div>
        <div v-if="memberTipSetting.boolValue === true" class="tw-mt-8">
          <div class="tw-flex tw-flex-col">
            <div class="tw-text-2xl tw-text-center">Leave a tip?</div>
            <base-list-group class="tw-mt-3">
              <base-list-item>
                <div class="tw-flex tw-justify-between">
                  <div class="tw-flex">
                    <base-radio :value="suggestedTip1.numValue" name="tipOption" v-model="tipOption" />
                    <div class="tw-text-lg">{{ suggestedTip1.numValue }} %</div>
                  </div>
                  <div class="tw-text-lg tw-font-normal">
                    {{ formatCurrency(Math.round((suggestedTip1.numValue / 100) * this.redeemCount * this.plan.creditUnitPrice), plan.currency) }}
                  </div>
                </div>
              </base-list-item>
              <base-list-item>
                <div class="tw-flex tw-justify-between">
                  <div class="tw-flex">
                    <base-radio :value="suggestedTip2.numValue" name="tipOption" v-model="tipOption" />
                    <div class="tw-text-lg">{{ suggestedTip2.numValue }} %</div>
                  </div>
                  <div class="tw-text-lg tw-font-normal">
                    {{ formatCurrency(Math.round((suggestedTip2.numValue / 100) * this.redeemCount * this.plan.creditUnitPrice), plan.currency) }}
                  </div>
                </div>
              </base-list-item>
              <base-list-item>
                <div class="tw-flex tw-justify-between">
                  <div class="tw-flex">
                    <base-radio :value="suggestedTip3.numValue" name="tipOption" v-model="tipOption" />
                    <div class="tw-text-lg">{{ suggestedTip3.numValue }} %</div>
                  </div>
                  <div class="tw-text-lg tw-font-normal">
                    {{ formatCurrency(Math.round((suggestedTip3.numValue / 100) * this.redeemCount * this.plan.creditUnitPrice), plan.currency) }}
                  </div>
                </div>
              </base-list-item>
              <base-list-item>
                <div class="tw-flex tw-justify-between">
                  <div class="tw-flex">
                    <base-radio :value="suggestedTip4.numValue" name="tipOption" v-model="tipOption" />
                    <div class="tw-text-lg">{{ suggestedTip4.numValue }} %</div>
                  </div>
                  <div class="tw-text-lg tw-font-normal">
                    {{ formatCurrency(Math.round((suggestedTip4.numValue / 100) * this.redeemCount * this.plan.creditUnitPrice), plan.currency) }}
                  </div>
                </div>
              </base-list-item>
              <base-list-item>
                <div class="tw-flex tw-justify-between">
                  <div class="tw-flex">
                    <base-radio value="custom" name="tipOption" v-model="tipOption" />
                    <div class="tw-text-lg tw-my-auto">Custom</div>
                  </div>
                  <base-form ref="customTipForm" class="tw-my-auto">
                    <base-currency-input
                      class="tw-my-auto"
                      v-if="tipOption === 'custom'"
                      v-model="formattedTipAmount"
                      :int-value.sync="tipAmount"
                      :rules="[() => tipAmount > 50 || 'Value must be greater than $0.50', () => (tipOption === 'custom' && tipAmount > 0) || 'You must enter a tip value']"
                    />
                  </base-form>
                </div>
              </base-list-item>
              <base-list-item :showBottomBorder="false">
                <div class="tw-flex tw-justify-between">
                  <div class="tw-flex">
                    <base-radio value="none" name="tipOption" v-model="tipOption" />
                    <div class="tw-text-lg">No Tip</div>
                  </div>
                  <div class="tw-text-lg tw-font-normal">
                    {{ formatCurrency(0, plan.currency) }}
                  </div>
                </div>
              </base-list-item>
            </base-list-group>
          </div>
        </div>
        <base-button type="primary" size="lg" class="tw-w-full tw-mt-6" @click="redeemCredits()" :loading="isLoading">Redeem</base-button>
      </div>

      <!-- Success -->
      <div v-else-if="isSuccess === true">
        <div class="tw-bg-green-100 tw-w-full tw-flex tw-justify-center tw-items-center tw-rounded-t-lg">
          <check-icon class="tw-w-24 tw-h-24 tw-text-green-500" />
        </div>
        <div class="tw-flex-col tw-justify-center tw-mt-6 tw-pb-4 tw-px-4">
          <div class="tw-text-xl tw-text-thin tw-text-center">Success</div>
          <div class="tw-text-gray-600 tw-text-center tw-mt-2 ">Thanks for coming in! You're all set!</div>
          <div class="tw-flex tw-justify-center tw-mt-4">
            <base-button class="tw-w-full sm:tw-w-1/2" type="primary" color="green" @click="closeAndRefresh">Continue</base-button>
          </div>
        </div>
      </div>
    </base-card>
  </base-modal>
</template>
<script>
import { ShoppingBagIcon, MinusIcon, PlusIcon, CheckIcon } from "@/assets/svgs";
import InlineError from "@/components/InlineError";
import { mapGetters } from "vuex";
import { capitalize } from "lodash";

export default {
  components: { ShoppingBagIcon, MinusIcon, PlusIcon, CheckIcon, InlineError },
  name: "CreditRedeem",
  props: {
    businessId: String,
    userId: String,
    memberFullName: String,
    plan: Object,
  },
  data() {
    return {
      isLoading: false,
      redeemCount: 1,
      showNegativeCreditsWarning: false,
      showRechargeAndRedeemWarning: false,
      showMaxCreditWarning: false,
      showNegativeCreditNotAllowed: false,
      showInactiveMessage: false,
      showLocationError: false,
      activeLocation: null,
      isConfirming: false,
      isSuccess: false,
      tipOption: null,
      formattedTipAmount: null,
      tipAmount: 0,
      settings: [],
      errorTitle: "",
      errorMessage: "",
      error: null,
    };
  },
  computed: {
    ...mapGetters("location", ["location", "locations"]),
    ...mapGetters("organization", ["organization"]),
    title() {
      return this.isConfirming ? capitalize(this.organization.customerLabelSingular) + " Confirmation" : "Redeem Credits";
    },
    description() {
      return this.isConfirming ? `Please turn the screen so the ${this.organization.customerLabelSingular} can confirm their credit usage` : "How many credits are being redeemed?";
    },
    rechargeAndRedeemCreditsCount() {
      return Math.max(this.minimumCreditRechargeSetting.numValue, this.redeemCount - this.plan.creditsBalance);
    },
    memberConfirmSetting() {
      return this.settings.length > 0
        ? this.settings.find((setting) => setting.settingConstant === "MEMBER_REDEEM_CONFIRM")
        : { businessSettingId: null, businessId: this.businessId, settingConstant: "MEMBER_REDEEM_CONFIRM", boolValue: false, stringValue: null, numValue: null };
    },
    memberTipSetting() {
      return this.settings.length > 0
        ? this.settings.find((setting) => setting.settingConstant === "MEMBER_REDEEM_TIP")
        : { businessSettingId: null, businessId: this.businessId, settingConstant: "MEMBER_REDEEM_TIP", boolValue: false, stringValue: null, numValue: null };
    },
    insufficientCreditsSetting() {
      return this.settings.length > 0
        ? this.settings.find((setting) => setting.settingConstant === "INSUFFICIENT_CREDITS")
        : { businessSettingId: null, businessId: this.businessId, settingConstant: "INSUFFICIENT_CREDITS", boolValue: null, stringValue: "DONT_ALLOW_REDEMPTION", numValue: null };
    },
    minimumCreditRechargeSetting() {
      return this.settings.length > 0
        ? this.settings.find((setting) => setting.settingConstant === "MINIMUM_CREDIT_RECHARGE")
        : { businessSettingId: null, businessId: this.businessId, settingConstant: null, boolValue: null, stringValue: null, numValue: 10 };
    },
    maxCreditsSetting() {
      return this.settings.length > 0
        ? this.settings.find((setting) => setting.settingConstant === "MAX_REDEEMABLE_CREDITS")
        : { businessSettingId: null, businessId: this.businessId, settingConstant: "MAX_REDEEMABLE_CREDITS", boolValue: false, stringValue: null, numValue: 999 };
    },
    suggestedTip1() {
      return this.settings.length > 0
        ? this.settings.find((setting) => setting.settingConstant === "TIP_LEVEL_CENTS_1")
        : { businessSettingId: null, businessId: this.businessId, settingConstant: "TIP_LEVEL_CENTS_1", boolValue: false, stringValue: "USD", numValue: 100 };
    },
    suggestedTip2() {
      return this.settings.length > 0
        ? this.settings.find((setting) => setting.settingConstant === "TIP_LEVEL_CENTS_2")
        : { businessSettingId: null, businessId: this.businessId, settingConstant: "TIP_LEVEL_CENTS_2", boolValue: false, stringValue: "USD", numValue: 150 };
    },
    suggestedTip3() {
      return this.settings.length > 0
        ? this.settings.find((setting) => setting.settingConstant === "TIP_LEVEL_CENTS_3")
        : { businessSettingId: null, businessId: this.businessId, settingConstant: "TIP_LEVEL_CENTS_3", boolValue: false, stringValue: "USD", numValue: 200 };
    },
    suggestedTip4() {
      return this.settings.length > 0
        ? this.settings.find((setting) => setting.settingConstant === "TIP_LEVEL_CENTS_4")
        : { businessSettingId: null, businessId: this.businessId, settingConstant: "TIP_LEVEL_CENTS_4", boolValue: false, stringValue: "USD", numValue: 250 };
    },
    isCreditAmountValid() {
      return !this.showNegativeCreditNotAllowed && !this.showMaxCreditWarning && this.redeemCount > 0;
    },
  },
  created() {
    this.loadRedeemSettings();
  },
  mounted() {
    if (this.plan.creditsBalance < 1) {
      if (this.insufficientCreditsSetting.stringValue === "DONT_ALLOW_REDEMPTION") {
        this.showNegativeCreditNotAllowed = true;
        this.redeemCount = 0;
      } else if (this.insufficientCreditsSetting.stringValue === "ALLOW_NEGATIVE_CREDITS") {
        this.showNegativeCreditsWarning = true;
      } else if (this.insufficientCreditsSetting.stringValue === "RECHARGE_AND_REDEEM") {
        this.showRechargeAndRedeemWarning = true;
      }
    } else {
      this.showNegativeCreditsWarning = false;
      this.showRechargeAndRedeemWarning = false;
    }

    if (!this.location && !this.activeLocation && this.locations.length == 1) this.activeLocation = this.locations[0].businessLocationId;

    if (this.location) this.activeLocation = this.location.businessLocationId;
  },
  methods: {
    reset() {
      this.redeemCount = 1;
      this.isConfirming = false;
      this.isSuccess = false;
      this.showNegativeCreditsWarning = false;
      this.showRechargeAndRedeemWarning = false;
      this.showMaxCreditWarning = false;
      this.showNegativeCreditNotAllowed = false;
      this.showInactiveMessage = false;
      this.showLocationError = false;
      this.tipOption = null;
      this.tipAmount = 0;
      this.errorTitle = "";
      this.errorMessage = "";
      this.error = null;
    },
    show() {
      this.$refs.modal.show();
    },
    hide() {
      this.$refs.modal.hide();
    },
    add() {
      this.redeemCount++;
    },
    subtract() {
      if (this.redeemCount === 0) return;
      this.redeemCount--;
    },
    redeemCredits() {
      this.showLocationError = false;

      if (!this.activeLocation) {
        this.showLocationError = true;
        return;
      }

      if (this.memberTipSetting.boolValue === true) if (!this.$refs.customTipForm.validate()) return;

      this.isLoading = true;

      this.$axios
        .post(`/businesses/${this.businessId}/members/${this.userId}/plans/${this.plan.userBusinessPlanId}/credits/redeem`, {
          redeemCount: this.redeemCount,
          locationId: this.activeLocation,
          tipAmountinCents: this.tipAmount,
          currency: this.plan.currency,
        })
        .then(() => {
          if (this.isConfirming) {
            this.isSuccess = true;
          } else {
            this.closeAndRefresh();
          }
        })
        .catch((err) => {
          this.errorTitle = "Error redeeming credits";
          this.errorMessage = "We were not able to redeem your credits. Please reload the page to try again.";
          this.error = err;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    loadRedeemSettings() {
      let url = `/businesses/${this.businessId}/settings`;

      this.$axios.get(url).then((res) => {
        this.settings = res.data;
      });
    },
    closeAndRefresh() {
      this.EventBus.$emit("member_update");
      this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Credits Redeemed", message: "You successfully redeemed " + this.redeemCount + " credits" });
      this.$emit("refresh");
      this.hide();
    },
    startConfirmation() {
      this.showLocationError = false;

      if (!this.activeLocation) {
        this.showLocationError = true;
        return;
      }
      this.isConfirming = true;
    },
    formatCurrency(cents, currency) {
      return new Intl.NumberFormat(window.navigator.language, { style: "currency", currency: currency }).format(cents / 100);
    },
  },
  watch: {
    tipOption(value) {
      if (value === "none" || value === "custom") {
        this.tipAmount = 0;
      } else this.tipAmount = Math.round((value / 100) * this.redeemCount * this.plan.creditUnitPrice);
    },
    redeemCount(newValue) {
      if (this.maxCreditsSetting.numValue < newValue) {
        this.showMaxCreditWarning = true;
        return;
      } else this.showMaxCreditWarning = false;

      if (this.plan.status != "Active" && newValue > this.plan.creditsBalance) {
        this.showInactiveMessage = true;
        return;
      } else this.showInactiveMessage = false;

      if (this.insufficientCreditsSetting.stringValue === "ALLOW_NEGATIVE_CREDITS") {
        if (newValue < 1) {
          return;
        }
        if (newValue > this.plan.creditsBalance) {
          this.showNegativeCreditsWarning = true;
        } else {
          this.showNegativeCreditsWarning = false;
        }
      } else if (this.insufficientCreditsSetting.stringValue === "DONT_ALLOW_REDEMPTION") {
        if (newValue < 1) {
          return;
        } else if (newValue > this.plan.creditsBalance) {
          this.showNegativeCreditNotAllowed = true;
          return;
        } else {
          this.showNegativeCreditNotAllowed = false;
        }
      } else if (this.insufficientCreditsSetting.stringValue === "RECHARGE_AND_REDEEM") {
        if (newValue < 1) {
          return;
        } else if (newValue > this.plan.creditsBalance) {
          this.showRechargeAndRedeemWarning = true;
          return;
        } else {
          this.showRechargeAndRedeemWarning = false;
        }
      }
    },
    location() {
      if (location !== null) this.activeLocation = this.location.businessLocationId;
    },
  },
};
</script>
